import { ApolloProvider } from '@apollo/client';
import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import { colors, Config } from '@modernanimal/common';
import Onboarding from '@modernanimal/common/src/onboarding';
import { ClientStoreType } from '@modernanimal/common/src/shared/flows/onboarding/store';
import { globalCSS } from '@modernanimal/common/src/web';
import {
    Outlet, useLocation, useNavigate, useSearchParams
} from 'react-router-dom';
import useBreakpoint from 'use-breakpoint';

import apolloClient from './apolloClient';
import Fonts from './fonts';
import { BREAKPOINTS } from './helpers';
import Icons from './icons';

const MA_CONTACT_PHONE = '(866) 505-8755';
const MA_CONTACT_PHONE_LINK = 'tel:+18665058755';

const OnboardingEntry = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobileWeb = breakpoint === 'mobile';
    const isExistingAccountScreen = location.pathname.includes('/download-app');

    // Get location from URL - needed on app start
    const [searchParams] = useSearchParams();
    const locationId = searchParams.get('location');

    const homeUrl = Config.ApiRoot;

    const handleBack = () => {
        window.open(homeUrl, '_self');
    };

    const handleDone = (client: ClientStoreType) => {
        if (client?.id) {
            navigate('download-app');
            return;
        }
        window.open(homeUrl, '_self');
    };

    return (
        <>
            <Global styles={globalCSS} />
            <ApolloProvider client={apolloClient}>
                <Page>
                    <HeaderContainer isMobile={isMobileWeb}>
                        <HeaderSpacer />
                        <Icons.MaLogo />
                        <CallToBookButton>
                            <PhoneText>Call to book</PhoneText>
                            <PhoneLink href={`${MA_CONTACT_PHONE_LINK}`}>{` ${MA_CONTACT_PHONE}`}</PhoneLink>
                        </CallToBookButton>
                    </HeaderContainer>
                    <WebBookingContainer id="web-booking-container" isMobile={isMobileWeb}>
                        <OuterBodyContainer>
                            {isExistingAccountScreen ? (
                                <Outlet />
                            ) : (
                                <Onboarding
                                    onCancel={handleBack}
                                    onDone={handleDone}
                                    locationId={locationId ?? undefined}
                                    webBookingCustomStyles={{
                                        height: !isMobileWeb ? window.innerHeight - 110 : window.innerHeight,
                                        paddingBottom: 0,
                                    }}
                                    webBookingOverrides={{
                                        isMobileWeb,
                                    }}
                                />
                            )}
                        </OuterBodyContainer>
                    </WebBookingContainer>
                </Page>
            </ApolloProvider>
        </>
    );
};

export default OnboardingEntry;

const Page = styled.div`
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background-color: ${colors.BONE};
`;

const WebBookingContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1; /* take the remaining height after header */
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    background-color: ${colors.BONE};
    padding-top: 27px;
    overflow-y: auto;

    ${({ isMobile }) => isMobile && `
        padding-top: 0;
    `}
`;

const OuterBodyContainer = styled.div`
    width: 100%;
    max-width: 600px;
`;

const HeaderContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 72px;
    padding: 0 72px;
    background-color: ${colors.BONE};
    border-bottom: 1px solid ${colors.BROWN};

    ${({ isMobile }) => isMobile
    && `
        display: none;
    `}
`;

const CallToBookButton = styled.div`
    border: none;
    background: none;
    width: 211px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PhoneText = styled(Fonts.LabelMedium)`
    color: ${colors.BROWN};
    margin-right: 4px;
`;

const HeaderSpacer = styled.div`
    width: 211px;
`;

const PhoneLink = styled(Fonts.LabelMedium.withComponent('a'))`
    color: ${colors.BROWN};
    text-decoration: underline;
`;
